/* ==========================================================
   Scent — Bundle Builder
   ----------------------------------------------------------
   Free-form bundle composer:

     - Customer freely picks any combination of products from
       the three product-type groups (Diffuseurs / Parfums /
       Bougies). Click a card to add (qty 1), click again to
       remove. No slots, no caps.

     - The merchant defines a ladder of discount rules. Each
       rule has minimum-quantity conditions per type plus an
       optional total-items minimum, and yields a percentage
       or fixed-amount discount. ALL conditions in a rule
       must be satisfied for it to match.

     - Among matched rules, the one giving the highest savings
       is the "applied" rule. Its tag lights up in the ladder
       and its savings appear in the summary bar.

   Cart submission
   ---------------
     POST /cart/add.js with each line bearing
       properties._bundle_id   = applied rule slug (or 'free')
       properties._bundle_role = product type
     and cart-level attributes.bundle_active = rule slug.

     If cart_mode = "discount_redirect" and the rule has a
     discount_code, redirect to /discount/CODE?redirect=/cart
     after the add. Else just go to /cart.
   ========================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'scent_bundle_picks_v2';

  /* The builder is split across independent, reorderable sections (one
     Controller + N Category sections + a Rules section). The engine mounts
     ONCE and coordinates them document-wide, re-scanning when the theme editor
     adds/removes/reorders a section. */
  var appMounted = false;
  var appRescan = null;
  var appState = {};

  /* Each Category section emits {key,label,collection,pack_eligible,...}. */
  function readCategories() {
    var cats = {};
    document.querySelectorAll('[data-scent-bundle-category]').forEach(function (el) {
      try {
        var c = JSON.parse(el.textContent);
        if (c && c.key) cats[c.key] = c;
      } catch (e) {}
    });
    return cats;
  }
  /* Rules sections emit { rules: [...] } — concatenated if there's more than one. */
  function readRules() {
    var out = [];
    document.querySelectorAll('[data-scent-bundle-rules]').forEach(function (el) {
      try {
        var j = JSON.parse(el.textContent);
        if (j && j.rules) out = out.concat(j.rules);
      } catch (e) {}
    });
    return out;
  }

  function initApp() {
    if (appMounted) return;
    var controller = document.querySelector('[data-scent-bundle-controller]');
    if (!controller) return;
    appMounted = true;

    /* `root` is the document — cards/groups/rules live across sections.
       `controller` is used only for the config blob, summary refs and cart
       settings. */
    var root = document;

    var config = {};
    var configEl = controller.querySelector('[data-scent-bundle-config]');
    if (configEl) {
      try { config = JSON.parse(configEl.textContent); }
      catch (e) { console.warn('[scent-bundle] config parse failed', e); }
    }

    var moneyFormat = config.moneyFormat || '{{amount}}';
    var categories = readCategories();
    var rules = readRules();
    var packCfg = config.pack || null;

    /* ----- State ----- */
    /** picks[productId] = { productId, variantId, type, title, image, price, qty } */
    var picks = {};

    /** Pack Découverte mode. Mutually exclusive with the free-form bundle:
        when active, only parfums can be picked (capped at `target`), and the
        cart submission becomes a single pack-product line carrying the chosen
        parfum titles as line-item properties. Pricing is driven by the pack
        variant's price + compare_at_price, not by the rules ladder. */
    var packState = {
      active: false,
      variantId: null,
      target: 0,
      picks: {},
      price: 0,
      compareAt: 0,
      variantTitle: ''
    };

    restorePicks();

    /* ----- DOM refs ----- re-collected on rescan, since any section
       (including the controller) can be re-rendered by the theme editor. ----- */
    var summary, groupCountEls, ruleEls, packPanel, packHint, packHintMsg;
    function collectRefs() {
      summary = {
        root:      controller.querySelector('[data-bundle-summary]'),
        thumbs:    controller.querySelector('[data-bundle-thumbs]'),
        subtotal:  controller.querySelector('[data-bundle-subtotal]'),
        savings:   controller.querySelector('[data-bundle-savings]'),
        total:     controller.querySelector('[data-bundle-total]'),
        submit:    controller.querySelector('[data-bundle-submit]'),
        submitLbl: controller.querySelector('[data-bundle-submit-label]'),
        msg:       controller.querySelector('[data-bundle-msg]')
      };
      groupCountEls = document.querySelectorAll('[data-bundle-group-count]');
      ruleEls = document.querySelectorAll('[data-bundle-rule]');
      packPanel = document.querySelector('[data-bundle-pack]');
      packHint = document.querySelector('[data-bundle-pack-hint]');
      packHintMsg = document.querySelector('[data-bundle-pack-hint-msg]');
      /* Relocate the jump-to-cart button to <body> so its fixed position is
         relative to the viewport, independent of where the controller section
         sits in the DOM. */
      var jumpEl = document.querySelector('[data-bundle-jump]');
      if (jumpEl && jumpEl.parentNode !== document.body) document.body.appendChild(jumpEl);
      refreshJumpObserver();
    }

    /* The jump button is only useful when the cart summary is off-screen, so we
       watch the summary with an IntersectionObserver and add `is-hidden` to the
       button while the summary is visible. Re-pointed at the (possibly new)
       summary element every time refs are collected (e.g. after a rescan). */
    var jumpObserver = null;
    function refreshJumpObserver() {
      var jumpBtn = document.querySelector('[data-bundle-jump]');
      if (!jumpBtn) return;
      var target = (summary && summary.root)
        ? (summary.root.querySelector('.scent-bundle__summary-inner') || summary.root)
        : null;
      if (!('IntersectionObserver' in window) || !target) {
        jumpBtn.classList.remove('is-hidden');
        return;
      }
      if (jumpObserver) jumpObserver.disconnect();
      jumpObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          jumpBtn.classList.toggle('is-hidden', entry.isIntersecting);
        });
      }, { threshold: 0 });
      jumpObserver.observe(target);
    }

    collectRefs();

    /* ----- Card click → toggle selection.
       Also handles the small × button on each summary thumbnail, the variant
       pills/swatches on each card, the Pack Découverte variant chips, the
       Pack exit buttons, and the clickable rule cards (engage handler). ----- */
    root.addEventListener('click', function (e) {
      // Jump-to-cart button: smooth-scroll the cart summary into view.
      var jumpHit = e.target.closest('[data-bundle-jump]');
      if (jumpHit) {
        e.preventDefault();
        var jumpTarget = (summary && summary.root)
          ? (summary.root.querySelector('.scent-bundle__summary-inner') || summary.root)
          : null;
        if (jumpTarget && jumpTarget.scrollIntoView) {
          jumpTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      var submitBtn = e.target.closest('[data-bundle-submit]');
      if (submitBtn) {
        e.preventDefault();
        if (!submitBtn.disabled) submit();
        return;
      }
      var rmBtn = e.target.closest('[data-bundle-thumb-remove]');
      if (rmBtn) {
        e.preventDefault();
        e.stopPropagation();
        removePickById(parseInt(rmBtn.getAttribute('data-bundle-thumb-remove'), 10));
        return;
      }
      var packExit = e.target.closest('[data-bundle-pack-exit]');
      if (packExit) {
        e.preventDefault();
        e.stopPropagation();
        packDeactivate();
        return;
      }
      var packVariantBtn = e.target.closest('[data-bundle-pack-variant]');
      if (packVariantBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (packVariantBtn.classList.contains('is-unavailable') || packVariantBtn.disabled) return;
        var vid = parseInt(packVariantBtn.getAttribute('data-variant-id'), 10);
        if (packState.active && packState.variantId === vid) {
          packDeactivate();
        } else {
          packActivate(vid);
        }
        return;
      }
      /* Rule-popup variant: add the Pack Découverte as a standalone line
         item to the bundle summary. We bypass pack-mode (which is for the
         discovery PDP's in-bundle parfum-picking flow) and instead add a
         regular pick whose productId matches the rule's required_products,
         so the Pack Découverte rule's intrinsic compare-at savings kick in
         naturally through applyRules(). */
      var rulePopupVariantBtn = e.target.closest('[data-bundle-rule-popup-variant]');
      if (rulePopupVariantBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (rulePopupVariantBtn.disabled || rulePopupVariantBtn.classList.contains('is-unavailable')) return;
        var rpp = rulePopupVariantBtn.closest('[data-bundle-rule-popup]');
        if (!rpp) return;
        var packProductId = parseInt(rpp.getAttribute('data-rule-popup-pack-id'), 10) || 0;
        if (!packProductId) return;

        // Legacy: if pack-mode was somehow active (e.g. carried from another
        // session), tear it down before we switch to line-item mode.
        if (packState.active) packDeactivate();

        var rpvId      = parseInt(rulePopupVariantBtn.getAttribute('data-variant-id'), 10) || 0;
        var rpvPrice   = parseInt(rulePopupVariantBtn.getAttribute('data-variant-price'), 10) || 0;
        var rpvCompare = parseInt(rulePopupVariantBtn.getAttribute('data-variant-compare'), 10) || 0;
        var rpvLabel   = rulePopupVariantBtn.getAttribute('data-variant-label') || '';
        var packTitle  = rpp.getAttribute('data-rule-popup-pack-title') || 'Pack Découverte';
        var packHandle = rpp.getAttribute('data-rule-popup-pack-handle') || '';
        var priceEl    = rulePopupVariantBtn.querySelector('.scent-bundle__rule-popup-variant-price');
        var priceMoney = priceEl ? priceEl.textContent.trim() : '';

        // Toggle: clicking the already-active variant removes the pack.
        if (picks[packProductId] && picks[packProductId].variantId === rpvId) {
          delete picks[packProductId];
          flashMessage('Pack ' + rpvLabel + ' retiré de votre sélection.');
        } else {
          picks[packProductId] = {
            productId: packProductId,
            variantId: rpvId,
            type:      'pack',
            title:     packTitle + (rpvLabel ? ' · ' + rpvLabel : ''),
            handle:    packHandle,
            image:     '',
            price:     rpvPrice,
            compareAt: rpvCompare > rpvPrice ? rpvCompare : rpvPrice,
            priceMoney: priceMoney,
            fallbackLabel: rpvLabel,
            qty: 1
          };
          flashMessage('Pack ' + rpvLabel + ' ajouté à votre sélection.');
        }
        persistPicks();
        render();
        closeAllRulePopups();
        if (summary.root && summary.root.scrollIntoView) {
          summary.root.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
        return;
      }
      var rulePopupClose = e.target.closest('[data-bundle-rule-popup-close]');
      if (rulePopupClose) {
        e.preventDefault();
        e.stopPropagation();
        closeAllRulePopups();
        return;
      }
      /* Deficit prompt: clicking anywhere on the popup scrolls to the
         group that still needs picks. We read the target group from the
         action button's data attr, populated by updateEngagedRulePrompt(). */
      var promptWrap = e.target.closest('[data-bundle-rule-prompt]');
      if (promptWrap && !promptWrap.hasAttribute('hidden')) {
        e.preventDefault();
        e.stopPropagation();
        var actionEl = promptWrap.querySelector('[data-bundle-rule-prompt-action]');
        var target = actionEl ? actionEl.getAttribute('data-bundle-rule-prompt-target') : '';
        if (target) scrollToGroup(target);
        return;
      }
      var ruleCard = e.target.closest('[data-bundle-engage]');
      if (ruleCard) {
        e.preventDefault();
        e.stopPropagation();
        /* Pack rule click toggles the variant-picker popup instead of
           jumping straight into pack mode. The popup variant click (above)
           is what actually activates the pack. */
        if (ruleCard.getAttribute('data-rule-is-pack') === '1') {
          var rs = ruleCard.getAttribute('data-bundle-rule');
          var matchingPopup = root.querySelector('[data-bundle-rule-popup="' + rs + '"]');
          if (matchingPopup) {
            toggleRulePopup(matchingPopup);
            return;
          }
        }
        engageRule(ruleCard);
        return;
      }
      var variantBtn = e.target.closest('[data-bundle-variant]');
      if (variantBtn) {
        e.preventDefault();
        e.stopPropagation();
        switchVariant(variantBtn);
        return;
      }
      var card = e.target.closest('[data-bundle-card]');
      if (!card || card.getAttribute('aria-disabled') === 'true') return;
      togglePick(card);
    });

    function removePickById(productId) {
      if (!productId) return;
      var card = root.querySelector('[data-bundle-card][data-product-id="' + productId + '"]');
      // Pack mode: thumbnail × removes from packState.picks, not the main store.
      if (packState.active && packState.picks[productId]) {
        delete packState.picks[productId];
        if (card) {
          card.classList.remove('is-selected');
          card.setAttribute('aria-pressed', 'false');
        }
        render();
        return;
      }
      if (!picks[productId]) return;
      delete picks[productId];
      if (card) {
        card.classList.remove('is-selected');
        card.setAttribute('aria-pressed', 'false');
      }
      render();
      persistPicks();
    }

    /* ----- Outside-click + Esc: close any open rule popup ----- */
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) {
        closeAllRulePopups();
        return;
      }
      // Click inside the wrap but outside the open popup → close.
      var openWrap = root.querySelector('[data-bundle-rule-card].is-popup-open');
      if (!openWrap) return;
      if (openWrap.contains(e.target)) return;
      closeAllRulePopups();
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllRulePopups();
    });

    /* ----- Keyboard activation for role=button card ----- */
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      if (e.target.closest('[data-bundle-variant]')) return;
      var card = e.target.closest('[data-bundle-card]');
      if (!card || card.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      togglePick(card);
    });

    /* Submit is handled by the document click delegate above so it survives
       a controller re-render in the theme editor (no stale button listener). */

    /* ===== Selection logic ===== */

    function togglePick(card) {
      var productId = parseInt(card.getAttribute('data-product-id'), 10);
      if (!productId) return;

      // Pack Découverte mode: only cards in a pack-eligible category can be
      // picked, capped at the variant's target. Anything else is blocked with
      // an inline message so the customer understands why nothing happens.
      if (packState.active) {
        var packGroupEl = card.closest('[data-bundle-group]');
        var packEligible = packGroupEl && packGroupEl.getAttribute('data-bundle-pack-eligible') === '1';
        if (!packEligible) {
          flashMessage('Quittez le pack pour ajouter d\'autres articles.');
          return;
        }
        if (packState.picks[productId]) {
          delete packState.picks[productId];
          card.classList.remove('is-selected');
          card.setAttribute('aria-pressed', 'false');
        } else {
          if (Object.keys(packState.picks).length >= packState.target) {
            flashMessage('Votre pack est complet. Retirez un parfum pour en changer.');
            return;
          }
          var packPick = buildPick(card);
          if (!packPick) return;
          packState.picks[productId] = packPick;
          card.classList.add('is-selected');
          card.setAttribute('aria-pressed', 'true');
        }
        render();
        return;
      }

      if (picks[productId]) {
        delete picks[productId];
        card.classList.remove('is-selected');
        card.setAttribute('aria-pressed', 'false');
      } else {
        var pick = buildPick(card);
        if (!pick) return;
        picks[productId] = pick;
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
      }
      render();
      persistPicks();
    }

    /* ===== Pack Découverte mode ===== */

    function packTargetFromVariantTitle(title) {
      if (!title) return 0;
      var m = String(title).match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }

    function packActivate(variantId) {
      if (!packCfg || !packCfg.variants) return;
      var v = packCfg.variants.find(function (x) { return x.id === variantId; });
      if (!v || !v.available) return;

      var wasActive = packState.active;
      var carryPicks = {};
      if (wasActive) {
        // Switching variants — preserve existing parfum picks where they fit.
        // If the new target is smaller, keep only the first N (insertion order).
        var newTarget = packTargetFromVariantTitle(v.option1 || v.title);
        var keys = Object.keys(packState.picks);
        keys.slice(0, newTarget).forEach(function (k) { carryPicks[k] = packState.picks[k]; });
        // Strip is-selected from cards that are getting trimmed.
        keys.slice(newTarget).forEach(function (k) {
          var card = root.querySelector('[data-bundle-card][data-product-id="' + k + '"]');
          if (card) {
            card.classList.remove('is-selected');
            card.setAttribute('aria-pressed', 'false');
          }
        });
      } else {
        // Fresh entry — tear down any free-form picks so the two selection
        // sets don't render at the same time.
        clearAllPickClasses();
        picks = {};
        persistPicks();
      }

      packState.active = true;
      packState.variantId = variantId;
      packState.variantTitle = v.title || v.option1 || '';
      packState.target = packTargetFromVariantTitle(v.option1 || v.title);
      packState.price = v.price;
      packState.compareAt = (v.compare_at && v.compare_at > v.price) ? v.compare_at : v.price;
      packState.picks = carryPicks;

      // Pack mode takes over from any rule-deficit prompt that may have
      // been showing.
      engagedRuleSlug = null;

      // Update variant chip states on the section-level pack panel and
      // every rule-popup, so the active size is highlighted everywhere.
      root.querySelectorAll('[data-bundle-pack-variant], [data-bundle-rule-popup-variant]').forEach(function (b) {
        var bid = parseInt(b.getAttribute('data-variant-id'), 10);
        b.classList.toggle('is-active', bid === variantId);
        b.setAttribute('aria-pressed', bid === variantId ? 'true' : 'false');
      });

      // Reveal pack panel chrome (exit button, progress counter) + jump to
      // parfums so the customer immediately sees where to make their picks.
      // Skip the scroll when switching variants from inside an active session
      // — the customer is already in the right place.
      updatePackChrome();
      if (!wasActive) scrollToGroup(firstPackEligibleKey());
      render();
    }

    /* First category flagged pack-eligible (typically parfums) — where pack
       picks are made. Falls back to 'parfum' for legacy setups. */
    function firstPackEligibleKey() {
      var keys = Object.keys(categories);
      for (var i = 0; i < keys.length; i++) {
        if (categories[keys[i]] && categories[keys[i]].pack_eligible) return keys[i];
      }
      return 'parfum';
    }

    function packDeactivate() {
      packState.active = false;
      packState.variantId = null;
      packState.target = 0;
      packState.picks = {};
      packState.price = 0;
      packState.compareAt = 0;
      packState.variantTitle = '';

      root.querySelectorAll('[data-bundle-pack-variant], [data-bundle-rule-popup-variant]').forEach(function (b) {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });
      // Clear any selection styling that pack-mode added.
      clearAllPickClasses();
      // Re-hydrate the free-form picks (restored from storage) onto the cards.
      hydrateSelectedClasses();
      updatePackChrome();
      render();
    }

    function clearAllPickClasses() {
      root.querySelectorAll('[data-bundle-card].is-selected').forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });
    }

    function updatePackChrome() {
      if (!packPanel) return;
      var active = packState.active;
      packPanel.classList.toggle('is-active', active);

      // Pack price + compare display on the deal card.
      var priceEl = packPanel.querySelector('[data-bundle-pack-price]');
      var compareEl = packPanel.querySelector('[data-bundle-pack-compare]');
      var savingsEl = packPanel.querySelector('[data-bundle-pack-savings]');
      var progressEl = packPanel.querySelector('[data-bundle-pack-progress]');
      var countEl = packPanel.querySelector('[data-bundle-pack-count]');
      var targetEl = packPanel.querySelector('[data-bundle-pack-target]');
      var exitEl = packPanel.querySelector('[data-bundle-pack-exit]');

      if (active) {
        if (priceEl) priceEl.textContent = money(packState.price);
        if (compareEl) {
          if (packState.compareAt > packState.price) {
            compareEl.textContent = money(packState.compareAt);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        var saving = Math.max(0, packState.compareAt - packState.price);
        if (savingsEl) {
          if (saving > 0) {
            savingsEl.textContent = 'Économisez ' + money(saving);
            savingsEl.hidden = false;
          } else {
            savingsEl.hidden = true;
          }
        }
        if (progressEl) progressEl.hidden = false;
        if (countEl) countEl.textContent = String(Object.keys(packState.picks).length);
        if (targetEl) targetEl.textContent = String(packState.target);
        if (exitEl) exitEl.hidden = false;
      } else {
        // Idle: show the default (first variant) price/compare from the panel's
        // server-rendered defaults. We restore from the [data-bundle-pack] el's
        // initial state by re-reading the first available variant from packCfg.
        if (packCfg && packCfg.variants && packCfg.variants.length) {
          var first = packCfg.variants.find(function (v) { return v.available; }) || packCfg.variants[0];
          if (priceEl) priceEl.textContent = money(first.price);
          if (compareEl) {
            if (first.compare_at > first.price) {
              compareEl.textContent = money(first.compare_at);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }
          if (savingsEl) {
            var s = Math.max(0, first.compare_at - first.price);
            if (s > 0) {
              savingsEl.textContent = 'Économisez ' + money(s);
              savingsEl.hidden = false;
            } else {
              savingsEl.hidden = true;
            }
          }
        }
        if (progressEl) progressEl.hidden = true;
        if (exitEl) exitEl.hidden = true;
      }

      // Pack hint banner inside the parfums group.
      if (packHint) {
        if (active) {
          var remaining = Math.max(0, packState.target - Object.keys(packState.picks).length);
          var msg;
          if (remaining === 0) {
            msg = packCfg && packCfg.complete_label ? packCfg.complete_label : 'Pack complet, prêt à ajouter au panier.';
          } else {
            var tpl = (packCfg && packCfg.pick_instruction) || 'Plus que {count} parfum(s) à choisir pour compléter votre pack.';
            msg = tpl.replace('{count}', String(remaining));
          }
          if (packHintMsg) packHintMsg.innerHTML = msg;
          packHint.hidden = false;
        } else {
          packHint.hidden = true;
        }
      }
    }

    /* ===== Engaged rule (deficit prompt state) =====
       When a non-pack rule is clicked, its slug is parked here so the
       prompt banner under the card can track "still need X parfum(s)"
       until the deficit clears. Replaced when another rule is clicked,
       cleared when the rule applies cleanly or when pack mode takes over. */
    var engagedRuleSlug = null;

    /* ===== Clickable rule engagement =====
       Clicking a rule card auto-picks its required products (if any) and
       scrolls to whichever product group still has unmet minimums, so the
       customer sees a clear next step.

       Special case: the "Pack Découverte indicator" rule has no conditions
       of its own. Clicking it normally toggles its variant-picker popup
       (handled in the section click delegate above). Only if the popup
       isn't rendered — e.g. the merchant set `is_pack_rule` without pinning
       a product — does this function fall back to the legacy section-level
       pack activation. */
    function engageRule(card) {
      if (card.getAttribute('data-rule-is-pack') === '1') {
        if (!packCfg || !packCfg.variants) {
          flashMessage('Aucun produit Pack Découverte n\'est configuré.');
          return;
        }
        if (!packState.active) {
          var first = packCfg.variants.find(function (v) { return v.available; }) || packCfg.variants[0];
          if (first) packActivate(first.id);
        }
        if (packPanel && packPanel.scrollIntoView) {
          packPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      // If pack mode is active, exit it first — a regular rule and the pack
      // can't coexist in the same cart.
      if (packState.active) packDeactivate();
      // A new rule engagement supersedes any previous deficit prompt.
      closeAllRulePopups();
      engagedRuleSlug = card.getAttribute('data-bundle-rule') || null;

      var req = (card.getAttribute('data-rule-required-products') || '')
        .split(',').map(function (s) { return parseInt(s, 10); }).filter(Boolean);

      var missingRequired = [];
      req.forEach(function (id) {
        if (picks[id]) return;
        var target = root.querySelector('[data-bundle-card][data-product-id="' + id + '"]');
        if (target && target.getAttribute('aria-disabled') !== 'true') {
          var pick = buildPick(target);
          if (pick) {
            picks[id] = pick;
            target.classList.add('is-selected');
            target.setAttribute('aria-pressed', 'true');
          }
        } else {
          missingRequired.push(id);
        }
      });
      persistPicks();
      render(); // render() ends with updateEngagedRulePrompt()

      var deficits = evaluateRuleDeficits(card);
      if (deficits.length) {
        var first = deficits[0];
        scrollToGroup(first.key);
        flashMessage('Choisissez ' + first.need + ' ' + first.label + ' pour activer cette offre.');
      } else if (missingRequired.length) {
        flashMessage('Certains produits requis ne sont plus disponibles.');
        engagedRuleSlug = null;
        updateEngagedRulePrompt();
      } else {
        flashMessage('Offre activée. Ajoutez au panier pour valider.');
        engagedRuleSlug = null;
        updateEngagedRulePrompt();
      }
    }

    /* ===== Rule popup helpers ===== */
    function toggleRulePopup(popup) {
      if (!popup) return;
      var wasOpen = !popup.hasAttribute('hidden');
      closeAllRulePopups();
      if (!wasOpen) {
        popup.removeAttribute('hidden');
        var wrap = popup.closest('[data-bundle-rule-card]');
        if (wrap) wrap.classList.add('is-popup-open');
        syncRulePopupActive(popup);
      }
    }
    /* Highlight the currently-picked variant inside a rule-popup. Reads
       from `picks` (line-item mode, used by the bundle page) and falls
       back to packState (discovery PDP's in-bundle pack mode). */
    function syncRulePopupActive(popup) {
      if (!popup) return;
      var packProductId = parseInt(popup.getAttribute('data-rule-popup-pack-id'), 10) || 0;
      var activeId = 0;
      if (packProductId && picks[packProductId]) activeId = picks[packProductId].variantId;
      else if (packState.active && packState.variantId) activeId = packState.variantId;
      popup.querySelectorAll('[data-bundle-rule-popup-variant]').forEach(function (b) {
        var bid = parseInt(b.getAttribute('data-variant-id'), 10);
        b.classList.toggle('is-active', bid === activeId);
      });
    }
    function closeAllRulePopups() {
      root.querySelectorAll('[data-bundle-rule-popup]').forEach(function (p) {
        p.setAttribute('hidden', '');
      });
      root.querySelectorAll('[data-bundle-rule-card].is-popup-open').forEach(function (w) {
        w.classList.remove('is-popup-open');
      });
    }

    /* Build a packCfg-equivalent payload from a rule popup's DOM so the
       existing packActivate() flow works even when the section-level
       Pack Découverte product isn't configured. */
    function buildPackCfgFromPopup(popupEl) {
      if (!popupEl) return null;
      var btns = popupEl.querySelectorAll('[data-bundle-rule-popup-variant]');
      if (!btns.length) return null;
      var variants = [];
      btns.forEach(function (b) {
        var priceEl = b.querySelector('.scent-bundle__rule-popup-variant-price');
        var compareEl = b.querySelector('.scent-bundle__rule-popup-variant-compare');
        var priceTxt = priceEl ? priceEl.textContent.trim() : '';
        var compareTxt = compareEl ? compareEl.textContent.trim() : priceTxt;
        variants.push({
          id: parseInt(b.getAttribute('data-variant-id'), 10),
          available: !b.disabled,
          price: parseInt(b.getAttribute('data-variant-price'), 10) || 0,
          compare_at: parseInt(b.getAttribute('data-variant-compare'), 10) || 0,
          price_money: priceTxt,
          compare_money: compareTxt,
          option1: b.getAttribute('data-variant-label') || '',
          title: b.getAttribute('data-variant-label') || ''
        });
      });
      return {
        productId: parseInt(popupEl.getAttribute('data-rule-popup-pack-id'), 10) || 0,
        handle: popupEl.getAttribute('data-rule-popup-pack-handle') || '',
        title: popupEl.getAttribute('data-rule-popup-pack-title') || '',
        variants: variants
      };
    }

    /* ===== Deficit prompt =====
       Recomputes the engaged rule's deficits and either shows a banner
       under that rule's card or hides every prompt if the rule applies
       cleanly. Called from render() so it stays in sync with picks. */
    /* Parse a rule card's "key:min,key:min" conditions string into objects. */
    function parseConditions(str) {
      var out = [];
      (str || '').split(',').forEach(function (pair) {
        var kv = pair.split(':');
        if (kv.length === 2) {
          var k = kv[0].trim();
          var m = parseInt(kv[1], 10) || 0;
          if (k && m > 0) out.push({ key: k, min: m });
        }
      });
      return out;
    }
    function evaluateRuleDeficits(card) {
      var conds = parseConditions(card.getAttribute('data-rule-conditions'));
      var c = countByType();
      var deficits = [];
      conds.forEach(function (cond) {
        var have = c[cond.key] || 0;
        if (have < cond.min) {
          var label = (categories[cond.key] && categories[cond.key].label) || cond.key;
          deficits.push({ key: cond.key, need: cond.min - have, label: label });
        }
      });
      return deficits;
    }
    function updateEngagedRulePrompt() {
      var prompts = root.querySelectorAll('[data-bundle-rule-prompt]');
      prompts.forEach(function (p) { p.setAttribute('hidden', ''); });
      if (!engagedRuleSlug) return;
      var card = root.querySelector('[data-bundle-engage][data-bundle-rule="' + engagedRuleSlug + '"]');
      if (!card) { engagedRuleSlug = null; return; }
      var deficits = evaluateRuleDeficits(card);
      if (!deficits.length) {
        // Resolved — quietly clear state. The CTA flash already fires from
        // engageRule; we don't want it firing again every render.
        engagedRuleSlug = null;
        return;
      }
      var prompt = root.querySelector('[data-bundle-rule-prompt="' + engagedRuleSlug + '"]');
      if (!prompt) return;
      var first = deficits[0];
      var label = first.label;
      var ruleName = card.getAttribute('data-rule-name') || 'cette offre';
      var textEl = prompt.querySelector('[data-bundle-rule-prompt-text]');
      if (textEl) {
        textEl.innerHTML = 'Ajoutez <strong>' + first.need + ' ' + label
          + '</strong> pour activer ' + ruleName + '.';
      }
      var actionBtn = prompt.querySelector('[data-bundle-rule-prompt-action]');
      if (actionBtn) {
        actionBtn.removeAttribute('hidden');
        actionBtn.setAttribute('data-bundle-rule-prompt-target', first.key);
      }
      prompt.removeAttribute('hidden');
    }

    function scrollToGroup(key) {
      var group = root.querySelector('[data-bundle-group="' + key + '"]');
      if (!group || group.hasAttribute('hidden')) return;
      var head = group.querySelector('.scent-bundle__group-head') || group;
      if (head.scrollIntoView) head.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    var flashTimer = null;
    function flashMessage(text) {
      if (!summary.msg) return;
      summary.msg.textContent = text;
      summary.msg.classList.remove('is-error');
      clearTimeout(flashTimer);
      flashTimer = setTimeout(function () { if (summary.msg) summary.msg.textContent = ''; }, 4000);
    }

    function switchVariant(btn) {
      var card = btn.closest('[data-bundle-card]');
      if (!card) return;
      if (btn.classList.contains('is-unavailable') || btn.disabled) return;

      var newVariantId = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!newVariantId || parseInt(card.getAttribute('data-active-variant'), 10) === newVariantId) return;

      card.querySelectorAll('[data-bundle-variant]').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
      card.querySelectorAll('.scent-bundle-card__img').forEach(function (img) {
        img.classList.toggle('is-active', String(img.getAttribute('data-variant-id')) === String(newVariantId));
      });
      card.setAttribute('data-active-variant', newVariantId);

      var variants = parseVariants(card);
      var match = variants.find(function (v) { return v.id === newVariantId; });
      var priceEl = card.querySelector('[data-bundle-card-price]');
      if (priceEl && match) {
        priceEl.textContent = match.available ? match.price_money : 'Rupture';
      }

      // If this card is already picked, update the pick's variant + price.
      var productId = parseInt(card.getAttribute('data-product-id'), 10);
      if (picks[productId] && match && match.available) {
        picks[productId] = buildPick(card);
        render();
        persistPicks();
      }
    }

    function buildPick(card) {
      var productId = parseInt(card.getAttribute('data-product-id'), 10);
      var variantId = parseInt(card.getAttribute('data-active-variant'), 10);
      var variants = parseVariants(card);
      var match = variants.find(function (v) { return v.id === variantId; }) || variants[0];
      if (!match || !match.available) return null;

      var imgEl = card.querySelector('.scent-bundle-card__img.is-active');
      var imgSrc = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';

      var compareAt = (typeof match.compare_at === 'number' && match.compare_at > match.price)
        ? match.compare_at
        : match.price;
      return {
        productId: productId,
        variantId: match.id,
        type:      card.getAttribute('data-bundle-category-key') || card.getAttribute('data-product-type') || 'autre',
        title:     card.getAttribute('data-product-title') || '',
        handle:    card.getAttribute('data-product-handle') || '',
        image:     imgSrc,
        price:     match.price,
        compareAt: compareAt,
        priceMoney: match.price_money,
        qty:       1
      };
    }

    function parseVariants(card) {
      try { return JSON.parse(card.getAttribute('data-variants') || '[]'); }
      catch (_) { return []; }
    }

    /* ===== Counts and rule matching ===== */

    function countByType() {
      var c = { total: 0 };
      Object.keys(categories).forEach(function (k) { c[k] = 0; });
      Object.keys(picks).forEach(function (id) {
        var p = picks[id];
        c.total += p.qty;
        c[p.type] = (c[p.type] || 0) + p.qty;
      });
      return c;
    }

    /* Sum of a rule's category minimums + total minimum (used for tiebreak ordering). */
    function condSum(rule) {
      var s = rule.min_total || 0;
      var conds = rule.conditions || {};
      Object.keys(conds).forEach(function (k) { s += conds[k] || 0; });
      return s;
    }

    function ruleHasAnyCondition(rule) {
      var req = rule.required_products || [];
      var conds = rule.conditions || {};
      var anyCond = Object.keys(conds).some(function (k) { return conds[k] > 0; });
      return anyCond || rule.min_total > 0 || req.length > 0;
    }

    /* tryConsume — given a rule and a pool of still-available picks, attempt
       to satisfy the rule by consuming picks from the pool. Returns the array
       of consumed picks on success, or null if the rule's conditions can't be
       met from the pool. Greedy: pinned products first (exact matches), then
       type minimums from whatever's left, then the total-items minimum from
       any remaining types. */
    function tryConsume(rule, pool, reservedForOthers) {
      reservedForOthers = reservedForOthers || new Set();
      var consumed = [];
      var taken = new Set();
      var req = rule.required_products || [];

      // 1) Required products — exact product-ID matches. Reservation does
      //    not apply here: a rule's own required product is always free
      //    to consume, even if it appears in another rule's list.
      for (var i = 0; i < req.length; i++) {
        var reqId = req[i];
        var found = null;
        for (var j = 0; j < pool.length; j++) {
          if (!taken.has(j) && pool[j].productId === reqId) { found = j; break; }
        }
        if (found === null) return null;
        taken.add(found);
        consumed.push(pool[found]);
      }

      // findEligible — first pass skips reserved IDs so a generic slot
      // doesn't poach picks that belong to a stricter rule. Second pass
      // falls back to reserved picks so the rule still applies when the
      // customer literally has no other option.
      function findEligible(typeKey) {
        var idx = -1;
        for (var k = 0; k < pool.length; k++) {
          if (taken.has(k)) continue;
          if (typeKey && pool[k].type !== typeKey) continue;
          if (reservedForOthers.has(pool[k].productId)) continue;
          idx = k; break;
        }
        if (idx < 0) {
          for (var k2 = 0; k2 < pool.length; k2++) {
            if (taken.has(k2)) continue;
            if (typeKey && pool[k2].type !== typeKey) continue;
            idx = k2; break;
          }
        }
        return idx;
      }

      // 2) Category minimums — count what required already covered, fill the rest.
      var typeCounts = {};
      consumed.forEach(function (p) { typeCounts[p.type] = (typeCounts[p.type] || 0) + 1; });
      var conds = rule.conditions || {};
      var condKeys = Object.keys(conds);
      for (var t = 0; t < condKeys.length; t++) {
        var typeKey = condKeys[t];
        var need = Math.max(0, (conds[typeKey] || 0) - (typeCounts[typeKey] || 0));
        while (need > 0) {
          var idx = findEligible(typeKey);
          if (idx < 0) return null;
          taken.add(idx);
          consumed.push(pool[idx]);
          need--;
        }
      }

      // 3) Total minimum — any remaining picks of any type.
      var totalNeeded = Math.max(0, (rule.min_total || 0) - consumed.length);
      while (totalNeeded > 0) {
        var any = findEligible(null);
        if (any < 0) return null;
        taken.add(any);
        consumed.push(pool[any]);
        totalNeeded--;
      }

      return consumed;
    }

    function consumedSubtotal(consumed) {
      return consumed.reduce(function (s, p) { return s + p.price * p.qty; }, 0);
    }

    function consumedCompareAtSavings(consumed) {
      return consumed.reduce(function (s, p) {
        var diff = (p.compareAt || p.price) - p.price;
        return s + Math.max(0, diff) * p.qty;
      }, 0);
    }

    /* ruleSavings — how much DZD this rule actually saves the customer given
       the picks it consumed. Special case: a fixed_off rule with value 0
       means "use the consumed products' compare_at vs. price difference as
       the discount" — that's how the Pack Découverte rule renders its
       intrinsic savings without the merchant having to type a static
       number. */
    function ruleSavings(rule, consumed) {
      var sub = consumedSubtotal(consumed);
      var v = parseFloat(rule.discount_value) || 0;
      if (rule.discount_type === 'percentage') {
        var pct = Math.max(0, Math.min(100, v));
        return Math.round(sub * (pct / 100));
      }
      if (rule.discount_type === 'fixed_off') {
        if (v <= 0) {
          // Intrinsic / product-driven discount.
          return consumedCompareAtSavings(consumed);
        }
        return Math.min(sub, Math.round(v * 100));
      }
      return 0;
    }

    /* applyRules — stacks every matched rule, consuming picks as it goes so
       a single pick can't contribute to two rules at once. Most-constrained
       rules (more required_products, then more total minimums) try first so
       picks naturally route to the most-specific rule that wants them. */
    function applyRules(picksArr) {
      var pool = picksArr.slice();
      /* Sort:
           1. Rules with more `required_products` first (more specific picks
              are more constrained, less flexible).
           2. Tiebreaker: fewer type minimums (`min_parfums` etc.) first.
              A rule that pins all of its slots to specific products is
              stricter than a rule that fills extra slots with generic type
              minimums — so the strict one consumes its picks first, and the
              looser rule (e.g. "Duo Platinium": diffuser + any parfum) only
              gets what's left over. */
      var ordered = rules.filter(ruleHasAnyCondition).slice().sort(function (a, b) {
        var ra = (a.required_products || []).length;
        var rb = (b.required_products || []).length;
        if (rb !== ra) return rb - ra;
        return condSum(a) - condSum(b);
      });

      var applied = [];
      ordered.forEach(function (rule) {
        /* "Reserved" pick IDs: products required by some OTHER rule. When
           this rule's type-minimum slots are being filled, we prefer picks
           NOT in this set so a Trio-Lessive parfum doesn't get pulled into
           a Duo-Platinium slot when a more generic parfum is also picked.
           A fallback still allows reserved picks if nothing else fits, so
           the discount still applies when it's the customer's only option. */
        var ownReq = new Set(rule.required_products || []);
        var reservedForOthers = new Set();
        rules.forEach(function (other) {
          if (other === rule) return;
          (other.required_products || []).forEach(function (id) {
            if (!ownReq.has(id)) reservedForOthers.add(id);
          });
        });

        var consumed = tryConsume(rule, pool, reservedForOthers);
        if (!consumed) return;
        var sav = ruleSavings(rule, consumed);
        applied.push({ rule: rule, consumed: consumed, savings: sav });
        // Remove consumed picks from the pool so they can't feed another rule.
        consumed.forEach(function (p) {
          var i = pool.indexOf(p);
          if (i >= 0) pool.splice(i, 1);
        });
      });
      return applied;
    }

    /* ===== Render ===== */

    function flatPicks() { return Object.keys(picks).map(function (k) { return picks[k]; }); }
    function flatPackPicks() { return Object.keys(packState.picks).map(function (k) { return packState.picks[k]; }); }

    function render() {
      // Pack mode short-circuits the regular rules ladder. Pricing comes from
      // the pack variant's price + compare_at_price; thumbnails show only the
      // chosen parfums; CTA gates on the pack being filled to its target.
      if (packState.active) { renderPackMode(); updateEngagedRulePrompt(); return; }

      var c = countByType();
      var picksArr = flatPicks();
      var subtotal = picksArr.reduce(function (s, p) { return s + p.price * p.qty; }, 0);
      var appliedRules = applyRules(picksArr);
      var savings = appliedRules.reduce(function (s, a) { return s + a.savings; }, 0);
      var total = subtotal - savings;
      var hasPicks = c.total > 0;
      var primaryApplied = appliedRules[0] || null;

      // Thumbnails — each carries a small × button that removes the pick.
      if (summary.thumbs) {
        summary.thumbs.innerHTML = '';
        if (!hasPicks) {
          var span = document.createElement('span');
          span.className = 'scent-bundle__summary-thumb-empty';
          span.textContent = 'Aucune sélection pour l\'instant';
          summary.thumbs.appendChild(span);
        } else {
          flatPicks().forEach(function (p) {
            var t = document.createElement('span');
            t.className = 'scent-bundle__summary-thumb';
            if (p.type === 'pack') t.classList.add('scent-bundle__summary-thumb--pack');
            if (p.image) {
              var img = document.createElement('img');
              img.src = p.image;
              img.alt = p.title;
              img.loading = 'lazy';
              t.appendChild(img);
            } else if (p.fallbackLabel) {
              // Pack pick has no card image — surface its variant label
              // (e.g. "3x") so the thumb reads as something rather than empty.
              var lbl = document.createElement('span');
              lbl.className = 'scent-bundle__summary-thumb-label';
              lbl.textContent = p.fallbackLabel;
              t.appendChild(lbl);
            }
            var rm = document.createElement('button');
            rm.type = 'button';
            rm.className = 'scent-bundle__summary-thumb-remove';
            rm.setAttribute('aria-label', 'Retirer ' + p.title);
            rm.setAttribute('data-bundle-thumb-remove', String(p.productId));
            rm.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';
            t.appendChild(rm);
            t.title = p.title + ' · ' + (p.priceMoney || money(p.price));
            summary.thumbs.appendChild(t);
          });
        }
      }

      // Prices.
      var hasDiscount = savings > 0;
      if (summary.subtotal) {
        summary.subtotal.textContent = money(subtotal);
        summary.subtotal.hidden = !hasDiscount;
      }
      if (summary.savings) {
        var label = '';
        if (appliedRules.length === 1) {
          label = appliedRules[0].rule.name + ' · -' + money(appliedRules[0].savings);
        } else if (appliedRules.length > 1) {
          var names = appliedRules.map(function (a) { return a.rule.name; }).join(' + ');
          label = names + ' · -' + money(savings);
        }
        summary.savings.textContent = label;
        summary.savings.hidden = !hasDiscount;
      }
      if (summary.total) summary.total.textContent = money(total);

      // Group counters.
      groupCountEls.forEach(function (el) {
        var group = el.closest('[data-bundle-group]');
        if (!group) return;
        var key = group.getAttribute('data-bundle-group');
        var n = c[key] || 0;
        el.textContent = n + (n > 1 ? ' sélectionnés' : ' sélectionné');
        el.classList.toggle('is-active', n > 0);
      });

      // Rule ladder — every applied rule lights up. Each rule's displayed
      // value is overwritten with the actual money saved (important for the
      // intrinsic Pack Découverte case where the static value is 0 and the
      // savings come from compare_at minus price on the consumed picks).
      var appliedById = {};
      appliedRules.forEach(function (a) { appliedById[a.rule.id] = a; });
      ruleEls.forEach(function (el) {
        var id = el.getAttribute('data-bundle-rule');
        var entry = appliedById[id];
        el.classList.toggle('is-applied', !!entry);
        var valEl = el.querySelector('.scent-bundle__rule-value');
        if (!valEl) return;
        if (entry) {
          valEl.textContent = entry.savings > 0 ? '-' + money(entry.savings) : '';
        } else {
          var staticVal = parseFloat(el.getAttribute('data-rule-discount-value')) || 0;
          var staticType = el.getAttribute('data-rule-discount-type') || '';
          if (staticType === 'percentage' && staticVal > 0) {
            valEl.textContent = '-' + staticVal + '%';
          } else if (staticType === 'fixed_off' && staticVal > 0) {
            valEl.textContent = '-' + staticVal + ' DZD';
          } else {
            valEl.textContent = '';
          }
        }
      });

      // Submit.
      if (summary.submit) {
        summary.submit.disabled = !hasPicks;
        if (summary.submitLbl) {
          if (!hasPicks) {
            summary.submitLbl.textContent = 'Sélectionnez des produits';
          } else if (savings > 0) {
            summary.submitLbl.textContent = 'Ajouter au panier · -' + money(savings);
          } else {
            summary.submitLbl.textContent = 'Ajouter au panier';
          }
        }
      }

      if (summary.msg) {
        summary.msg.textContent = '';
        summary.msg.classList.remove('is-error');
      }

      // Slide the fixed summary bar in only when there's something selected.
      if (summary.root) summary.root.classList.toggle('is-visible', hasPicks);

      // Stash state for submission and ARIA.
      appState = {
        counts: c, subtotal: subtotal, savings: savings, total: total,
        appliedRules: appliedRules, applied: primaryApplied ? primaryApplied.rule : null
      };

      // Keep rule-popup chips in sync with current line-item picks so the
      // active variant is highlighted everywhere the customer might open it.
      root.querySelectorAll('[data-bundle-rule-popup]').forEach(syncRulePopupActive);

      updateEngagedRulePrompt();
    }

    function renderPackMode() {
      var packPicks = flatPackPicks();
      var picked = packPicks.length;
      var target = packState.target;
      var complete = target > 0 && picked >= target;
      var savings = Math.max(0, packState.compareAt - packState.price);

      // Thumbnails — only the parfum picks contribute. A "+" placeholder fills
      // remaining slots so the customer can see how many they still need.
      if (summary.thumbs) {
        summary.thumbs.innerHTML = '';
        if (!picked) {
          var span = document.createElement('span');
          span.className = 'scent-bundle__summary-thumb-empty';
          span.textContent = 'Sélectionnez ' + target + ' parfums pour votre pack';
          summary.thumbs.appendChild(span);
        } else {
          packPicks.forEach(function (p) {
            var t = document.createElement('span');
            t.className = 'scent-bundle__summary-thumb';
            if (p.image) {
              var img = document.createElement('img');
              img.src = p.image;
              img.alt = p.title;
              img.loading = 'lazy';
              t.appendChild(img);
            }
            var rm = document.createElement('button');
            rm.type = 'button';
            rm.className = 'scent-bundle__summary-thumb-remove';
            rm.setAttribute('aria-label', 'Retirer ' + p.title);
            rm.setAttribute('data-bundle-thumb-remove', String(p.productId));
            rm.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';
            t.appendChild(rm);
            t.title = p.title;
            summary.thumbs.appendChild(t);
          });
        }
      }

      // Prices — pack variant drives everything; subtotal = compare_at struck.
      if (summary.subtotal) {
        if (packState.compareAt > packState.price) {
          summary.subtotal.textContent = money(packState.compareAt);
          summary.subtotal.hidden = false;
        } else {
          summary.subtotal.hidden = true;
        }
      }
      if (summary.savings) {
        if (savings > 0) {
          summary.savings.textContent = 'Pack Découverte · -' + money(savings);
          summary.savings.hidden = false;
        } else {
          summary.savings.hidden = true;
        }
      }
      if (summary.total) summary.total.textContent = money(packState.price);

      // Group counters — pack picks only count for pack-eligible categories.
      groupCountEls.forEach(function (el) {
        var group = el.closest('[data-bundle-group]');
        if (!group) return;
        var eligible = group.getAttribute('data-bundle-pack-eligible') === '1';
        var n = eligible ? picked : 0;
        el.textContent = n + (n > 1 ? ' sélectionnés' : ' sélectionné');
        el.classList.toggle('is-active', n > 0);
      });

      // While in pack mode the regular rules can't match (their conditions
      // look at the free-form `picks`, which we cleared). The ONE rule that
      // does light up is the Pack Découverte indicator — its displayed
      // savings are rewritten to reflect the live pack variant's compare_at
      // minus price.
      ruleEls.forEach(function (el) {
        if (el.getAttribute('data-rule-is-pack') === '1') {
          el.classList.add('is-applied');
          var valEl = el.querySelector('.scent-bundle__rule-value');
          if (valEl) valEl.textContent = savings > 0 ? '-' + money(savings) : '';
        } else {
          el.classList.remove('is-applied');
        }
      });

      // Hint banner reflects current count.
      updatePackChrome();

      // CTA — enabled only when the pack is complete.
      if (summary.submit) {
        summary.submit.disabled = !complete;
        if (summary.submitLbl) {
          if (!picked) {
            summary.submitLbl.textContent = 'Choisissez ' + target + ' parfums';
          } else if (!complete) {
            summary.submitLbl.textContent = 'Plus que ' + (target - picked) + ' parfum' + ((target - picked) > 1 ? 's' : '');
          } else if (savings > 0) {
            summary.submitLbl.textContent = 'Ajouter le pack · -' + money(savings);
          } else {
            summary.submitLbl.textContent = 'Ajouter le pack au panier';
          }
        }
      }

      // Pack mode always shows the summary bar.
      if (summary.root) summary.root.classList.add('is-visible');

      appState = {
        mode: 'pack',
        target: target,
        picked: picked,
        price: packState.price,
        compareAt: packState.compareAt,
        savings: savings,
        packPicks: packPicks
      };
    }

    function hydrateSelectedClasses() {
      root.querySelectorAll('[data-bundle-card]').forEach(function (card) {
        var id = parseInt(card.getAttribute('data-product-id'), 10);
        if (picks[id]) {
          card.classList.add('is-selected');
          card.setAttribute('aria-pressed', 'true');
          // Sync variant to the persisted pick.
          var vBtn = card.querySelector('[data-bundle-variant][data-variant-id="' + picks[id].variantId + '"]');
          if (vBtn && !vBtn.classList.contains('is-active')) {
            card.querySelectorAll('[data-bundle-variant]').forEach(function (b) {
              b.classList.toggle('is-active', b === vBtn);
            });
            card.querySelectorAll('.scent-bundle-card__img').forEach(function (img) {
              img.classList.toggle('is-active', String(img.getAttribute('data-variant-id')) === String(picks[id].variantId));
            });
            card.setAttribute('data-active-variant', picks[id].variantId);
          }
        } else {
          card.classList.remove('is-selected');
          card.setAttribute('aria-pressed', 'false');
        }
      });
    }

    /* ===== Cart submission ===== */

    function submit() {
      // Pack mode submission: ONE cart line for the pack variant, carrying the
      // chosen parfum titles as line-item properties so the merchant sees what
      // to fulfil. No separate parfum lines — the pack price IS the price.
      if (packState.active) {
        return submitPack();
      }

      var c = countByType();
      if (c.total === 0) return;
      var st = appState || { applied: null, appliedRules: [] };
      var appliedList = st.appliedRules || [];
      var primaryRule = st.applied;
      var cartMode = config.cartMode || (controller && controller.getAttribute('data-cart-mode')) || 'items_with_attributes';
      var fallbackCode = config.fallbackDiscount || (controller && controller.getAttribute('data-fallback-discount')) || '';

      summary.submit.classList.add('is-loading');
      summary.submit.setAttribute('aria-busy', 'true');
      if (summary.msg) {
        summary.msg.textContent = 'Ajout au panier…';
        summary.msg.classList.remove('is-error');
      }

      // Map each pick to the rule that consumed it (if any) so the cart line
      // carries the correct bundle-rule attribution. Picks not consumed by
      // any rule get `_bundle_id: 'free'`.
      var pickRuleMap = {};
      appliedList.forEach(function (entry) {
        entry.consumed.forEach(function (p) { pickRuleMap[p.productId] = entry.rule.id; });
      });
      var stackedId = appliedList.length
        ? appliedList.map(function (a) { return a.rule.id; }).join('+')
        : 'free';
      var bundleId = stackedId;
      var items = flatPicks().map(function (p) {
        return {
          id: p.variantId,
          quantity: p.qty,
          properties: {
            _bundle_id:   pickRuleMap[p.productId] || 'free',
            _bundle_role: p.type
          }
        };
      });

      // First rule with a discount code wins as the redirect target.
      var firstCoded = appliedList.find(function (a) { return a.rule.discount_code; });
      var code = (firstCoded && firstCoded.rule.discount_code) || (primaryRule && primaryRule.discount_code) || fallbackCode || '';
      code = (code || '').trim();
      var wantsDiscountRedirect = (cartMode === 'discount_redirect' && code);
      var drawer = document.querySelector('cart-drawer');
      // Ask /cart/update.js to also render the cart-drawer section HTML so we
      // can hand it straight to the drawer's renderContents(). Skipped when
      // we're going to redirect to /discount/CODE anyway.
      var wantsDrawer = !!drawer && !wantsDiscountRedirect;

      addItems(items)
        .then(function () { return setBundleAttribute(bundleId, wantsDrawer); })
        .then(function (cart) {
          // Clear local picks now they're banked in the cart.
          picks = {};
          persistPicks();

          if (wantsDiscountRedirect) {
            window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
            return;
          }

          if (wantsDrawer && cart && cart.sections) {
            drawer.renderContents({ id: bundleId, sections: cart.sections });
            // Reset the bundle UI in place so the customer can start a new bundle.
            summary.submit.classList.remove('is-loading');
            summary.submit.removeAttribute('aria-busy');
            root.querySelectorAll('[data-bundle-card].is-selected').forEach(function (el) {
              el.classList.remove('is-selected');
              el.setAttribute('aria-pressed', 'false');
            });
            render();
            return;
          }

          // Fallback for themes without the cart-drawer custom element.
          window.location.href = '/cart';
        })
        .catch(function (err) {
          console.warn('[scent-bundle] submit failed', err);
          summary.submit.classList.remove('is-loading');
          summary.submit.removeAttribute('aria-busy');
          if (summary.msg) {
            summary.msg.textContent = err && err.message
              ? err.message
              : 'Une erreur est survenue. Veuillez réessayer.';
            summary.msg.classList.add('is-error');
          }
        });
    }

    function submitPack() {
      var packPicks = flatPackPicks();
      if (packPicks.length < packState.target || !packState.variantId) return;

      summary.submit.classList.add('is-loading');
      summary.submit.setAttribute('aria-busy', 'true');
      if (summary.msg) {
        summary.msg.textContent = 'Ajout au panier…';
        summary.msg.classList.remove('is-error');
      }

      // Build properties listing each chosen parfum on the pack line.
      // Numbered keys ("Parfum 1", "Parfum 2"…) so the cart displays them
      // in selection order. `_bundle_id` stays internal/hidden by Shopify
      // convention (underscore prefix).
      var props = { _bundle_id: 'pack-decouverte', _pack_size: String(packState.target) };
      packPicks.forEach(function (p, i) {
        props['Parfum ' + (i + 1)] = p.title;
      });

      var items = [{ id: packState.variantId, quantity: 1, properties: props }];
      var drawer = document.querySelector('cart-drawer');
      var wantsDrawer = !!drawer;

      addItems(items)
        .then(function () { return setBundleAttribute('pack-decouverte', wantsDrawer); })
        .then(function (cart) {
          if (wantsDrawer && cart && cart.sections) {
            drawer.renderContents({ id: 'pack-decouverte', sections: cart.sections });
            packDeactivate();
            summary.submit.classList.remove('is-loading');
            summary.submit.removeAttribute('aria-busy');
            render();
            return;
          }
          window.location.href = '/cart';
        })
        .catch(function (err) {
          console.warn('[scent-bundle] pack submit failed', err);
          summary.submit.classList.remove('is-loading');
          summary.submit.removeAttribute('aria-busy');
          if (summary.msg) {
            summary.msg.textContent = err && err.message ? err.message : 'Une erreur est survenue. Veuillez réessayer.';
            summary.msg.classList.add('is-error');
          }
        });
    }

    function addItems(items) {
      var endpoint = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) {
            var msg = body && body.description ? body.description : 'Cart add failed';
            throw new Error(msg);
          }
          return body;
        });
      });
    }

    function setBundleAttribute(slug, withDrawerSections) {
      var body = { attributes: { bundle_active: slug } };
      if (withDrawerSections) {
        // Shopify renders these sections into cart.sections{} on the response,
        // ready to feed into cart-drawer's renderContents().
        body.sections = 'cart-drawer,cart-icon-bubble';
        body.sections_url = window.location.pathname;
      }
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    /* ===== Persistence ===== */

    function persistPicks() {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify({ v: 2, ts: Date.now(), data: picks }));
      } catch (_) {}
    }

    function restorePicks() {
      try {
        var raw = sessionStorage.getItem(STORE_KEY);
        if (!raw) return;
        var parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== 2 || !parsed.data) return;
        Object.keys(parsed.data).forEach(function (k) {
          var p = parsed.data[k];
          if (p && p.productId && p.variantId) picks[p.productId] = p;
        });
      } catch (_) {}
    }

    /* ===== Money formatting ===== */

    function money(cents) {
      var amount = (cents / 100);
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, formatNumber(amount, '.', ',', 2))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formatNumber(amount, '.', ',', 0))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatNumber(amount, ',', '.', 2))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, formatNumber(amount, ',', '.', 0))
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, formatNumber(amount, ',', ' ', 2))
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, formatNumber(amount, ',', ' ', 0));
    }

    function formatNumber(amount, decSep, thouSep, decimals) {
      var sign = amount < 0 ? '-' : '';
      var abs = Math.abs(amount);
      var fixed = abs.toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      return sign + (parts.length === 2 ? parts[0] + decSep + parts[1] : parts[0]);
    }

    /* ===== Lazy-load all remaining products =====
       The Liquid `paginate by 50` tag caps the server render at 50 products
       per request. Anything beyond that is fetched here from /products.json
       (a public Shopify endpoint, max 250 per page) and rendered into the
       appropriate type group. Existing IDs are deduped, the group total
       counters update, and any persisted picks are re-hydrated onto the
       newly inserted cards so a refreshed customer sees their selections
       even if the matching product came in via AJAX. */

    function bucketize(typeStr) {
      var t = (typeStr || '').toLowerCase();
      if (t.indexOf('diffuseur') !== -1 || t.indexOf('diffuser') !== -1) return 'diffuseur';
      if (t.indexOf('bougie') !== -1 || t.indexOf('candle') !== -1) return 'bougie';
      if (t.indexOf('parfum') !== -1 || t.indexOf('perfume') !== -1 || t.indexOf('fragrance') !== -1) return 'parfum';
      return null;
    }

    function escapeHtml(s) {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    /* Color-name → hex map for AJAX-loaded cards.
       Server-rendered cards (first 50) get their swatch from
       `variant.metafields.custom.swatch_color`. Beyond that, /products.json
       gives us no metafields, so we derive a colour from the option1 name
       (e.g. "Or", "Noir", "Bleu Marine"). Falls back to neutral grey when
       no token in the name maps to a known colour. */
    var SWATCH_MAP = {
      'or':'#c9a96e','dore':'#c9a96e','doré':'#c9a96e','gold':'#c9a96e',
      'argent':'#b8b8b8','argente':'#b8b8b8','argenté':'#b8b8b8','silver':'#b8b8b8',
      'noir':'#1a1a1a','black':'#1a1a1a',
      'blanc':'#fafafa','white':'#fafafa','ivoire':'#f5ebd6','ivory':'#f5ebd6',
      'rouge':'#d0312d','red':'#d0312d',
      'bleu':'#2563eb','blue':'#2563eb','marine':'#1e3a8a','navy':'#1e3a8a',
      'turquoise':'#06b6d4','cyan':'#06b6d4',
      'vert':'#16a34a','green':'#16a34a','olive':'#6b7d2c',
      'rose':'#f9a8d4','pink':'#f9a8d4',
      'marron':'#6b4226','brun':'#6b4226','brown':'#6b4226','chocolat':'#3d2618',
      'gris':'#888888','gray':'#888888','grey':'#888888',
      'beige':'#d9c8a3','creme':'#f5e6d3','crème':'#f5e6d3','cream':'#f5e6d3',
      'violet':'#7c3aed','pourpre':'#7c3aed','purple':'#7c3aed',
      'jaune':'#fbbf24','yellow':'#fbbf24',
      'orange':'#ea580c',
      'naturel':'#d9c8a3','natural':'#d9c8a3',
      'transparent':'#e5e7eb','clair':'#e5e7eb','clear':'#e5e7eb',
      'cuivre':'#b87333','copper':'#b87333',
      'bronze':'#cd7f32','laiton':'#b5a642',
      'champagne':'#e6c89c','sable':'#d9b88a','sand':'#d9b88a','taupe':'#a89485'
    };

    function swatchFromName(name) {
      if (!name) return '#cccccc';
      var key = String(name).trim().toLowerCase();
      if (SWATCH_MAP[key]) return SWATCH_MAP[key];
      // Try matching any token in the name (e.g. "bleu marine", "or rose").
      var tokens = key.split(/[\s\-/&,_]+/).filter(Boolean);
      for (var i = 0; i < tokens.length; i++) {
        if (SWATCH_MAP[tokens[i]]) return SWATCH_MAP[tokens[i]];
      }
      // Last resort: a raw hex inside the option name.
      var hex = key.match(/#([0-9a-f]{3}|[0-9a-f]{6})\b/);
      if (hex) return '#' + hex[1];
      return '#cccccc';
    }

    function shopifyImg(url, width) {
      if (!url) return '';
      if (url.indexOf('//') === 0) url = 'https:' + url;
      // Insert _Wx before the file extension (before any ?query).
      var m = url.match(/^(.*?)(\.[a-zA-Z0-9]+)(\?.*)?$/);
      if (!m) return url;
      return m[1] + '_' + width + 'x' + m[2] + (m[3] || '');
    }

    function buildCardFromJson(p, key) {
      if (!p || !p.variants || !p.variants.length) return null;
      var firstAvail = null;
      for (var i = 0; i < p.variants.length; i++) {
        if (p.variants[i].available) { firstAvail = p.variants[i]; break; }
      }
      if (!firstAvail) firstAvail = p.variants[0];
      var soldOut = !firstAvail.available;

      // Variant-type detection: option1 name → color or size.
      var variantType = 'none';
      if (p.variants.length > 1 && p.options && p.options[0]) {
        var optName = (typeof p.options[0] === 'string' ? p.options[0] : (p.options[0].name || '')).toLowerCase();
        if (optName.indexOf('color') !== -1 || optName.indexOf('couleur') !== -1) variantType = 'color';
        else variantType = 'size';
      }

      var typeKey = key || 'autre';

      // Variants payload (price normalized to cents to match server-rendered cards).
      var payload = p.variants.map(function (v) {
        var cents = Math.round(parseFloat(v.price) * 100);
        var compareRaw = v.compare_at_price;
        var compareCents = compareRaw ? Math.round(parseFloat(compareRaw) * 100) : cents;
        return {
          id: v.id,
          available: !!v.available,
          price: cents,
          compare_at: compareCents,
          price_money: money(cents),
          option1: v.option1 || ''
        };
      });

      var card = document.createElement('div');
      card.setAttribute('role', 'button');
      card.className = 'scent-bundle-card' + (soldOut ? ' is-soldout' : '');
      card.setAttribute('data-bundle-card', '');
      card.setAttribute('data-product-id', String(p.id));
      card.setAttribute('data-bundle-category-key', typeKey);
      card.setAttribute('data-product-type', typeKey);
      card.setAttribute('data-product-title', p.title || '');
      card.setAttribute('data-product-handle', p.handle || '');
      // /products.json exposes tags either as an array or a comma-string. Normalize
      // to a comma-separated lowercase list so the chip filter sees both shapes
      // (matches the server-rendered card attribute).
      var tagsRaw = p.tags;
      var tagsStr = '';
      if (Array.isArray(tagsRaw)) tagsStr = tagsRaw.join(',');
      else if (typeof tagsRaw === 'string') tagsStr = tagsRaw;
      card.setAttribute('data-product-tags', tagsStr.toLowerCase());
      card.setAttribute('data-variant-type', variantType);
      card.setAttribute('data-active-variant', String(firstAvail.id));
      card.setAttribute('data-variants', JSON.stringify(payload));
      card.setAttribute('aria-pressed', 'false');
      card.setAttribute('tabindex', soldOut ? '-1' : '0');
      if (soldOut) card.setAttribute('aria-disabled', 'true');

      var titleParts = (p.title || '').split(' - ');
      var titleHtml = '<span>' + escapeHtml(titleParts[0]) + '</span>';
      if (titleParts.length > 1) titleHtml += '<em>' + escapeHtml(titleParts[titleParts.length - 1]) + '</em>';

      // One <img> per variant — mirrors the server-rendered snippet so the
      // switchVariant() toggle (which keys off data-variant-id) can swap to
      // the right image. Variants without a featured_image fall back to the
      // product's primary image so the card never goes blank on switch.
      var fallbackImg = (p.images && p.images[0] && p.images[0].src) || (p.image && p.image.src) || '';
      var anyImg = !!fallbackImg || p.variants.some(function (v) {
        return v.featured_image && v.featured_image.src;
      });
      var imageHtml;
      if (anyImg) {
        imageHtml = p.variants.map(function (v) {
          var src = (v.featured_image && v.featured_image.src) || fallbackImg;
          if (!src) return '';
          var imgUrl = shopifyImg(src, 800);
          return '<img class="scent-bundle-card__img' +
            (v.id === firstAvail.id ? ' is-active' : '') +
            '" data-variant-id="' + v.id +
            '" src="' + escapeHtml(imgUrl) +
            '" alt="' + escapeHtml(p.title || '') +
            '" loading="lazy">';
        }).join('');
      } else {
        imageHtml = '<div class="scent-bundle-card__img scent-bundle-card__img--placeholder is-active" data-variant-id="' + firstAvail.id + '"></div>';
      }

      var variantsHtml = '';
      if (variantType === 'size') {
        variantsHtml = '<div class="scent-bundle-card__variants scent-bundle-card__variants--size" data-bundle-card-variants>' +
          p.variants.map(function (v) {
            return '<button type="button" class="scent-bundle-card__pill' +
              (v.id === firstAvail.id ? ' is-active' : '') +
              (!v.available ? ' is-unavailable' : '') +
              '" data-bundle-variant data-variant-id="' + v.id + '"' +
              (!v.available ? ' disabled aria-disabled="true"' : '') + '>' +
              escapeHtml(v.option1 || '') + '</button>';
          }).join('') +
          '</div>';
      } else if (variantType === 'color') {
        variantsHtml = '<div class="scent-bundle-card__variants scent-bundle-card__variants--color" data-bundle-card-variants>' +
          p.variants.map(function (v) {
            // /products.json doesn't expose metafields, so the metafield-based
            // swatch_color isn't available here. Derive from option1 name via
            // SWATCH_MAP — falls back to neutral grey if nothing matches.
            var bg = swatchFromName(v.option1);
            return '<button type="button" class="scent-bundle-card__swatch' +
              (v.id === firstAvail.id ? ' is-active' : '') +
              (!v.available ? ' is-unavailable' : '') +
              '" data-bundle-variant data-variant-id="' + v.id + '"' +
              ' style="background-color: ' + bg + '"' +
              ' aria-label="' + escapeHtml(v.option1 || '') + '"' +
              (!v.available ? ' disabled aria-disabled="true"' : '') + '></button>';
          }).join('') +
          '</div>';
      }

      var priceText = soldOut ? 'Rupture' : money(Math.round(parseFloat(firstAvail.price) * 100));

      card.innerHTML =
        '<span class="scent-bundle-card__check" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24"><polyline points="5,12 10,17 19,7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span>' +
        '<div class="scent-bundle-card__image-wrap">' +
          '<h3 class="scent-bundle-card__title">' + titleHtml + '</h3>' +
          imageHtml +
        '</div>' +
        '<div class="scent-bundle-card__meta">' +
          '<span class="scent-bundle-card__price" data-bundle-card-price>' + escapeHtml(priceText) + '</span>' +
          variantsHtml +
        '</div>';

      return card;
    }

    function updateGroupTotal(typeKey) {
      var group = root.querySelector('[data-bundle-group="' + typeKey + '"]');
      if (!group) return;
      var n = group.querySelectorAll('[data-bundle-card]').length;
      var totalEl = group.querySelector('[data-group-total]');
      if (totalEl) totalEl.textContent = n + ' pièce' + (n > 1 ? 's' : '');
      // Reveal a previously-hidden group as soon as it has at least one card.
      if (n > 0 && group.hasAttribute('hidden')) group.removeAttribute('hidden');
    }

    function finalizeGroups() {
      // Hide categories that ended up empty after AJAX; reveal those with cards.
      document.querySelectorAll('[data-bundle-group]').forEach(function (group) {
        var n = group.querySelectorAll('[data-bundle-card]').length;
        if (n === 0) group.setAttribute('hidden', '');
        else group.removeAttribute('hidden');
      });
      var anyGroup = document.querySelector('[data-bundle-group] [data-bundle-card]');
      var notice = document.querySelector('[data-bundle-no-products]');
      if (notice && anyGroup) notice.remove();
    }

    /* Lazy-load the rest of each category's collection beyond the server-
       rendered first page (Liquid `paginate by 50`). Pulls from the public
       /collections/{handle}/products.json endpoint and appends bundle cards
       to that category's group. Runs once per group (guarded by data-loaded). */
    function loadCategoryProducts() {
      document.querySelectorAll('[data-bundle-group]').forEach(function (group) {
        if (group.dataset.bundleLoaded === '1') return;
        var key = group.getAttribute('data-bundle-category-key') || group.getAttribute('data-bundle-group');
        var handle = group.getAttribute('data-bundle-collection') || '';
        var container = group.querySelector('[data-bundle-group-products]');
        if (!handle || !container) { group.dataset.bundleLoaded = '1'; return; }
        group.dataset.bundleLoaded = '1';

        var existingIds = new Set();
        group.querySelectorAll('[data-bundle-card]').forEach(function (c) {
          var id = parseInt(c.getAttribute('data-product-id'), 10);
          if (id) existingIds.add(id);
        });

        var page = 1;
        var maxPages = 50; // safety cap

        function fetchPage() {
          return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page, {
            headers: { 'Accept': 'application/json' }
          })
            .then(function (r) { return r.ok ? r.json() : { products: [] }; })
            .then(function (j) {
              var products = (j && j.products) || [];
              if (!products.length) return false;
              products.forEach(function (p) {
                if (existingIds.has(p.id)) return;
                existingIds.add(p.id);
                var card = buildCardFromJson(p, key);
                if (card) container.appendChild(card);
              });
              updateGroupTotal(key);
              hydrateSelectedClasses();
              refreshAllBrowse();
              if (products.length < 250) return false;
              if (++page > maxPages) return false;
              return fetchPage();
            })
            .catch(function () { return false; });
        }

        fetchPage().then(function () { finalizeGroups(); refreshAllBrowse(); });
      });
    }

    /* ===== Browse controls (per-group chips + search + show-more) =====
       Each product group gets its own chip filter, search input, and
       overflow limit. State lives on the group element so multiple groups
       are independent. `applyFilters` is the single source of truth — it
       walks every card and decides visibility based on the active chip,
       search query, and the overflow limit.
       ----------------------------------------------------------------- */
    var browseCfg = (config.browse || {});
    var BROWSE_INITIAL = parseInt(browseCfg.initial_visible, 10) || 12;
    var CHIP_PREFIX    = (browseCfg.chip_prefix || '').toLowerCase();
    var ALL_LABEL      = browseCfg.all_label || 'Tous';

    /** State per group keyed by group element. */
    var groupBrowseState = new WeakMap();

    function getBrowseState(groupEl) {
      var s = groupBrowseState.get(groupEl);
      if (!s) {
        var initial = parseInt(groupEl.getAttribute('data-initial-visible'), 10) || BROWSE_INITIAL;
        // `expanded` is reset whenever the view changes (chip swap, search edit)
        // so a "Voir tout" click in one category doesn't carry over into another.
        s = { chip: '', query: '', expanded: false, initial: initial };
        groupBrowseState.set(groupEl, s);
      }
      return s;
    }

    function cardTags(card) {
      var raw = card.getAttribute('data-product-tags') || '';
      if (!raw) return [];
      return raw.split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    }

    /* Pulls the unique set of "chip" tags from the cards in a group. When a
       prefix is set, only tags starting with it are surfaced (and the prefix
       is stripped for display). When no prefix is set, every tag is shown. */
    function collectChipTags(groupEl) {
      var seen = new Map(); // key: tag-key (lowercase), value: { label, sort }
      groupEl.querySelectorAll('[data-bundle-card]').forEach(function (card) {
        cardTags(card).forEach(function (tag) {
          var key, label;
          if (CHIP_PREFIX) {
            if (tag.indexOf(CHIP_PREFIX) !== 0) return;
            key = tag;
            label = tag.slice(CHIP_PREFIX.length).trim();
            if (!label) return;
          } else {
            key = tag;
            label = tag;
          }
          if (!seen.has(key)) {
            // Title-case the label for display, preserving accented characters.
            var pretty = label.charAt(0).toUpperCase() + label.slice(1);
            seen.set(key, { key: key, label: pretty });
          }
        });
      });
      return Array.from(seen.values()).sort(function (a, b) {
        return a.label.localeCompare(b.label, 'fr');
      });
    }

    function renderChips(groupEl) {
      var host = groupEl.querySelector('[data-bundle-chips]');
      if (!host) return;
      var chips = collectChipTags(groupEl);
      var s = getBrowseState(groupEl);
      host.innerHTML = '';
      if (!chips.length) {
        host.parentNode && host.parentNode.classList.add('is-empty-chips');
        return;
      }
      host.parentNode && host.parentNode.classList.remove('is-empty-chips');

      var all = document.createElement('button');
      all.type = 'button';
      all.className = 'scent-bundle__chip' + (s.chip === '' ? ' is-active' : '');
      all.setAttribute('data-bundle-chip', '');
      all.textContent = ALL_LABEL;
      host.appendChild(all);

      chips.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'scent-bundle__chip' + (s.chip === c.key ? ' is-active' : '');
        b.setAttribute('data-bundle-chip', c.key);
        b.textContent = c.label;
        host.appendChild(b);
      });
    }

    /* The combined visibility pass: chip + search filter, then overflow limit.
       The overflow cap applies inside every chip view (including "Tous") and
       inside search results, so big categories like Parfums stay readable
       even after a filter narrows them. Selected cards bypass the cap so
       persisted picks never disappear. */
    function applyFilters(groupEl) {
      var s = getBrowseState(groupEl);
      var cards = groupEl.querySelectorAll('[data-bundle-card]');
      var filtersActive = !!s.chip || !!s.query;
      var query = s.query.toLowerCase();
      var visibleAfterFilters = 0;
      var overflowHiddenCount = 0;

      cards.forEach(function (card) {
        var matchesFilters = true;
        if (s.chip) {
          var tags = cardTags(card);
          if (tags.indexOf(s.chip) === -1) matchesFilters = false;
        }
        if (matchesFilters && query) {
          var title = (card.getAttribute('data-product-title') || '').toLowerCase();
          if (title.indexOf(query) === -1) matchesFilters = false;
        }
        if (!matchesFilters) {
          card.classList.add('is-hidden-by-filter');
          card.classList.remove('is-hidden-by-overflow');
          return;
        }
        card.classList.remove('is-hidden-by-filter');
        visibleAfterFilters++;

        var alreadySelected = card.classList.contains('is-selected');
        if (!s.expanded && visibleAfterFilters > s.initial && !alreadySelected) {
          card.classList.add('is-hidden-by-overflow');
          overflowHiddenCount++;
        } else {
          card.classList.remove('is-hidden-by-overflow');
        }
      });

      // Show-more button visibility/label.
      var btn = groupEl.querySelector('[data-bundle-show-more]');
      var lbl = groupEl.querySelector('[data-bundle-show-more-label]');
      if (btn) {
        if (s.expanded && visibleAfterFilters > s.initial) {
          btn.hidden = false;
          if (lbl) lbl.textContent = 'Voir moins';
        } else if (overflowHiddenCount > 0) {
          btn.hidden = false;
          if (lbl) lbl.textContent = 'Voir tout (' + visibleAfterFilters + ')';
        } else {
          btn.hidden = true;
        }
      }

      // Empty state.
      var empty = groupEl.querySelector('[data-bundle-no-results]');
      if (empty) empty.hidden = !(filtersActive && visibleAfterFilters === 0);
    }

    function bindGroupBrowse(groupEl) {
      if (groupEl.dataset.browseBound === '1') return;
      groupEl.dataset.browseBound = '1';

      var chipsHost = groupEl.querySelector('[data-bundle-chips]');
      if (chipsHost) {
        chipsHost.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-bundle-chip]');
          if (!btn) return;
          var s = getBrowseState(groupEl);
          var key = btn.getAttribute('data-bundle-chip') || '';
          s.chip = (s.chip === key) ? '' : key;
          s.expanded = false;  // each chip starts collapsed
          chipsHost.querySelectorAll('[data-bundle-chip]').forEach(function (b) {
            b.classList.toggle('is-active', (b.getAttribute('data-bundle-chip') || '') === s.chip);
          });
          applyFilters(groupEl);
        });
      }

      var searchInput = groupEl.querySelector('[data-bundle-search]');
      if (searchInput) {
        var debTimer = null;
        searchInput.addEventListener('input', function () {
          clearTimeout(debTimer);
          debTimer = setTimeout(function () {
            var s = getBrowseState(groupEl);
            var next = (searchInput.value || '').trim();
            if (next !== s.query) s.expanded = false;
            s.query = next;
            applyFilters(groupEl);
          }, 120);
        });
      }

      var showMore = groupEl.querySelector('[data-bundle-show-more]');
      if (showMore) {
        showMore.addEventListener('click', function () {
          var s = getBrowseState(groupEl);
          s.expanded = !s.expanded;
          applyFilters(groupEl);
          if (!s.expanded) {
            // Scroll the group head back into view when collapsing.
            var head = groupEl.querySelector('.scent-bundle__group-head');
            if (head && head.scrollIntoView) head.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    }

    function refreshAllBrowse() {
      root.querySelectorAll('[data-bundle-group]').forEach(function (g) {
        bindGroupBrowse(g);
        renderChips(g);
        applyFilters(g);
      });
    }

    /* ===== Rescan — re-read sections + refs after a theme-editor change,
       WITHOUT re-binding the document listeners (those are bound once). ===== */
    function rescan() {
      var ctrl = document.querySelector('[data-scent-bundle-controller]');
      if (ctrl) {
        controller = ctrl;
        var cEl = ctrl.querySelector('[data-scent-bundle-config]');
        if (cEl) {
          try {
            config = JSON.parse(cEl.textContent);
            moneyFormat = config.moneyFormat || moneyFormat;
            packCfg = config.pack || null;
          } catch (e) {}
        }
      }
      categories = readCategories();
      rules = readRules();
      collectRefs();
      hydrateSelectedClasses();
      updatePackChrome();
      refreshAllBrowse();
      render();
      loadCategoryProducts();
    }
    appRescan = rescan;

    /* ===== Public API for sibling sections (e.g. Scent — Bundle Offers) =====
       Lets a standalone product card add/remove itself from the bundle
       selection instead of going straight to the cart. Defined once (initApp
       runs once); the closure keeps `picks`, render(), etc. live across
       rescans. */
    window.ScentBundle = {
      ready: true,
      hasProduct: function (productId) {
        return !!picks[parseInt(productId, 10)];
      },
      /* Toggle a product in the free-form bundle selection.
         p = { productId, variantId, type, title, image, price, compareAt }
         Returns true if it ended up selected, false if removed/blocked. */
      toggleProduct: function (p) {
        if (!p) return false;
        var pid = parseInt(p.productId, 10);
        var vid = parseInt(p.variantId, 10);
        if (!pid || !vid) return false;
        if (packState.active) {
          flashMessage('Quittez le pack pour ajouter d\'autres articles.');
          return false;
        }
        var card = root.querySelector('[data-bundle-card][data-product-id="' + pid + '"]');
        if (picks[pid]) {
          delete picks[pid];
          if (card) { card.classList.remove('is-selected'); card.setAttribute('aria-pressed', 'false'); }
          render();
          persistPicks();
          return false;
        }
        var price = parseInt(p.price, 10) || 0;
        var compareAt = parseInt(p.compareAt, 10) || 0;
        picks[pid] = {
          productId: pid,
          variantId: vid,
          type: p.type || 'autre',
          title: p.title || '',
          image: p.image || '',
          price: price,
          compareAt: compareAt > price ? compareAt : price,
          qty: 1
        };
        if (card) { card.classList.add('is-selected'); card.setAttribute('aria-pressed', 'true'); }
        render();
        persistPicks();
        flashMessage('Ajouté à votre sélection.');
        return true;
      },
      scrollToCart: function () {
        var t = (summary && summary.root)
          ? (summary.root.querySelector('.scent-bundle__summary-inner') || summary.root)
          : null;
        if (t && t.scrollIntoView) t.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    /* Tell sections that loaded before the engine that the API is now live. */
    document.dispatchEvent(new CustomEvent('scent-bundle:ready'));

    /* ===== Boot ===== */
    hydrateSelectedClasses();
    updatePackChrome();
    render();
    refreshAllBrowse();
    loadCategoryProducts();
  }

  /* ===== Entry ===== */
  function boot() {
    if (document.querySelector('[data-scent-bundle-controller]')) initApp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: a section was added/removed/reordered/edited. Mount on first
     appearance of the controller; otherwise rescan in place so the document
     listeners are never double-bound. */
  document.addEventListener('shopify:section:load', function () {
    if (!appMounted) boot(); else if (appRescan) appRescan();
  });
  document.addEventListener('shopify:section:unload', function () {
    if (appMounted && appRescan) appRescan();
  });
})();
